import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Papa from 'papaparse'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Select } from '@/components/ui/Select'
import { DataTable } from '@/components/data-table/DataTable'
import { ArrowLeft, Upload, Download, AlertCircle, CheckCircle } from 'lucide-react'
import { useCreateCustomer } from '@/features/customers/hooks/useCustomers'
import { useClients } from '@/features/clients/hooks/useClients'
import { customerSchema } from '@/features/customers/schemas/customerSchema'
import { z } from 'zod'

interface ParsedRow {
  customer_name: string
  contact_person?: string
  phone?: string
  email?: string
  address?: string
}

interface ValidationResult {
  id: string
  row: number
  data: ParsedRow
  valid: boolean
  errors: string[]
}

export function CustomerImportPage() {
  const navigate = useNavigate()
  const { data: clients } = useClients()
  const createCustomer = useCreateCustomer()
  const [selectedClientId, setSelectedClientId] = useState<string>('')
  const [file, setFile] = useState<File | null>(null)
  const [parsed, setParsed] = useState<ValidationResult[]>([])
  const [importing, setImporting] = useState(false)
  const [importComplete, setImportComplete] = useState(false)
  const [importResults, setImportResults] = useState({ success: 0, failed: 0 })

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      setFile(selectedFile)
      parseFile(selectedFile)
    }
  }

  const parseFile = (file: File) => {
    Papa.parse<ParsedRow>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const validated = results.data.map((row, index) => validateRow(row, index + 2))
        setParsed(validated)
      },
      error: (error) => {
        alert(`CSV parsing error: ${error.message}`)
      },
    })
  }

  const validateRow = (row: ParsedRow, rowNumber: number): ValidationResult => {
    const errors: string[] = []

    if (!selectedClientId) {
      errors.push('Client must be selected before uploading')
    }

    // Validate customer data with Zod
    try {
      customerSchema.parse({
        name: row.customer_name,
        contact_person: row.contact_person || null,
        phone: row.phone || undefined,
        email: row.email || undefined,
        address: row.address || undefined,
      })
    } catch (error) {
      if (error instanceof z.ZodError) {
        errors.push(...error.errors.map(e => `${e.path.join('.')}: ${e.message}`))
      }
    }

    return {
      id: `row-${rowNumber}`,
      row: rowNumber,
      data: row,
      valid: errors.length === 0,
      errors,
    }
  }

  const handleImport = async () => {
    if (!selectedClientId) {
      alert('Please select a client first')
      return
    }

    const validRows = parsed.filter(r => r.valid)
    if (validRows.length === 0) {
      alert('No valid rows to import')
      return
    }

    setImporting(true)
    let success = 0
    let failed = 0

    for (const row of validRows) {
      try {
        await createCustomer.mutateAsync({
          client_id: selectedClientId,
          name: row.data.customer_name,
          contact_person: row.data.contact_person || null,
          phone: row.data.phone || null,
          email: row.data.email || null,
          address: row.data.address || null,
          tags: [],
        })
        success++
      } catch (error) {
        failed++
        console.error('Failed to import customer:', error)
      }
    }

    setImportResults({ success, failed })
    setImportComplete(true)
    setImporting(false)
  }

  const downloadTemplate = () => {
    const template = [
      ['customer_name', 'contact_person', 'phone', 'email', 'address'],
      ['ABC Construction', 'John Smith', '555-0100', 'john@abc.com', '123 Main St'],
      ['XYZ Farming', 'Jane Doe', '555-0200', 'jane@xyz.com', '456 Oak Ave'],
    ]
    const csv = Papa.unparse(template)
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'customer_import_template.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const validCount = parsed.filter(r => r.valid).length
  const invalidCount = parsed.filter(r => !r.valid).length

  const clientOptions = clients?.map(c => ({
    value: c.id,
    label: c.name,
  })) || []

  const columns = [
    { key: 'row', header: 'Row' },
    {
      key: 'valid',
      header: 'Status',
      render: (r: ValidationResult) => r.valid ? (
        <Badge variant="success"><CheckCircle className="h-3 w-3 mr-1" />Valid</Badge>
      ) : (
        <Badge variant="danger"><AlertCircle className="h-3 w-3 mr-1" />Invalid</Badge>
      )
    },
    { key: 'customer_name', header: 'Customer Name', render: (r: ValidationResult) => r.data.customer_name },
    { key: 'contact_person', header: 'Contact Person', render: (r: ValidationResult) => r.data.contact_person || '—' },
    { key: 'phone', header: 'Phone', render: (r: ValidationResult) => r.data.phone || '—' },
    { key: 'email', header: 'Email', render: (r: ValidationResult) => r.data.email || '—' },
    {
      key: 'errors',
      header: 'Errors',
      render: (r: ValidationResult) => r.errors.length > 0 ? (
        <ul className="text-xs text-red-600">
          {r.errors.map((err, i) => <li key={i}>{err}</li>)}
        </ul>
      ) : '—'
    },
  ]

  if (importComplete) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/clients')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Import Complete</h1>
              <p className="text-sm text-gray-600">Customer import results</p>
            </div>
          </div>
        </div>

        <Card>
          <div className="text-center py-8">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Import Complete</h2>
            <p className="text-gray-600 mb-6">
              Successfully imported {importResults.success} customers
              {importResults.failed > 0 && `, ${importResults.failed} failed`}
            </p>
            <div className="flex justify-center space-x-4">
              <Button onClick={() => navigate('/clients')}>View Clients</Button>
              <Button variant="secondary" onClick={() => {
                setFile(null)
                setParsed([])
                setImportComplete(false)
                setImportResults({ success: 0, failed: 0 })
              }}>
                Import Another File
              </Button>
            </div>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Import Customers</h1>
            <p className="text-sm text-gray-600">Bulk upload customers from CSV</p>
          </div>
        </div>
        <Button variant="secondary" onClick={downloadTemplate}>
          <Download className="h-4 w-4 mr-2" />Download Template
        </Button>
      </div>

      <Card className="mb-6">
        <h2 className="text-lg font-semibold mb-4">Select Client</h2>
        <p className="text-sm text-gray-600 mb-4">
          Choose the client that these customers belong to before uploading the CSV file.
        </p>
        <Select
          label="Client"
          value={selectedClientId}
          onChange={(e) => setSelectedClientId(e.target.value)}
          options={clientOptions}
          required
        />
      </Card>

      <Card className="mb-6">
        <h2 className="text-lg font-semibold mb-4">Upload CSV File</h2>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-sm text-gray-600 mb-4">
            Drop a CSV file here or click to browse
          </p>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="hidden"
            id="csv-upload"
            disabled={!selectedClientId}
          />
          <label
            htmlFor="csv-upload"
            className={`inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md text-white transition-colors ${
              selectedClientId
                ? 'bg-primary-600 hover:bg-primary-700 cursor-pointer'
                : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            Select File
          </label>
          {!selectedClientId && (
            <p className="text-sm text-amber-600 mt-2">
              Please select a client first
            </p>
          )}
          {file && (
            <p className="text-sm text-gray-600 mt-4">
              Selected: <span className="font-medium">{file.name}</span>
            </p>
          )}
        </div>
      </Card>

      {parsed.length > 0 && (
        <>
          <Card className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Validation Results</h2>
                <p className="text-sm text-gray-600">
                  {validCount} valid, {invalidCount} invalid
                </p>
              </div>
              <Button
                onClick={handleImport}
                disabled={validCount === 0 || importing}
                loading={importing}
              >
                Import {validCount} Customers
              </Button>
            </div>
          </Card>

          <Card>
            <DataTable
              data={parsed}
              columns={columns}
              emptyMessage="No data parsed"
            />
          </Card>
        </>
      )}
    </div>
  )
}
